import type { Workspace } from '@up2stay/up2promo-api-client';
import { SubscriptionStatus } from '@up2stay/up2promo-api-client';
import type { QueryClient } from '@tanstack/react-query';

import {
  type GetStartedChecklistSlice,
  parseGetStartedSlice,
} from '@/core/guidance/checklist-persistence';

import {
  clearPendingWorkspaceIntent,
  readPendingWorkspaceIntent,
} from './onboarding-intent';
import {
  getGuidanceProgress,
  updateGuidanceProgress,
} from '@/core/guidance/guidance-progress-client';

export const planOnboardingGateQueryKey = (workspaceId: string) =>
  ['plan-onboarding-gate', workspaceId] as const;

/** Full React Query key used by `usePlanOnboardingGate` (must stay in sync). */
export function planOnboardingGateFullQueryKey(workspace: Workspace) {
  return [
    ...planOnboardingGateQueryKey(workspace.id),
    workspace.subscriptionStatus ?? 'none',
    workspace.billingGrant?.grantedAt ?? 'none',
    workspace.billingGrant?.expiresAt ?? 'none',
  ] as const;
}

export function invalidatePlanOnboardingGate(
  queryClient: QueryClient,
  workspaceId?: string,
): void {
  if (workspaceId) {
    void queryClient.invalidateQueries({
      queryKey: planOnboardingGateQueryKey(workspaceId),
    });
    return;
  }
  void queryClient.invalidateQueries({ queryKey: ['plan-onboarding-gate'] });
}

/**
 * Optimistic plan-gate value so bootstrap does not flicker to `ready` (and
 * yank the wizard home) between workspace create and guidance refetch.
 */
export function setPlanOnboardingGateIncomplete(
  queryClient: QueryClient,
  workspace: Workspace,
  incomplete: boolean,
): void {
  queryClient.setQueryData(
    planOnboardingGateFullQueryKey(workspace),
    incomplete,
  );
}

/**
 * Active platform billing grant — same predicate as backend
 * `isBillingGrantActive` (null/omitted expiresAt = never expires).
 */
export function workspaceHasActiveBillingGrant(
  workspace: Workspace,
  now: Date = new Date(),
): boolean {
  const grant = workspace.billingGrant;
  if (!grant) return false;
  if (!grant.expiresAt) return true;
  return new Date(grant.expiresAt) > now;
}

/**
 * Paid / trial entitlement already mirrored on the workspace — plan step done.
 * Active billing grants also clear the gate (grants cancel Stripe and null
 * `subscriptionStatus`, so status alone is not enough).
 * Free stays `plan: free` with null subscriptionStatus, so Free confirmation
 * is tracked on Guidance Progress (`planConfirmed`), not these fields.
 */
export function workspaceHasBillingEntitlement(workspace: Workspace): boolean {
  if (workspaceHasActiveBillingGrant(workspace)) return true;
  const status = workspace.subscriptionStatus;
  return (
    status === SubscriptionStatus.ACTIVE ||
    status === SubscriptionStatus.TRIALING ||
    status === SubscriptionStatus.PAST_DUE
  );
}

/**
 * Incomplete when the user started intent-aware create (intent on server or
 * pending locally) but has not confirmed a plan / started trial, and has no
 * billing entitlement yet.
 */
export function isPlanStepIncomplete(
  slice: GetStartedChecklistSlice,
  workspace: Workspace,
): boolean {
  if (workspaceHasBillingEntitlement(workspace)) return false;
  if (slice.planConfirmed) return false;
  return Boolean(slice.intent) || Boolean(readPendingWorkspaceIntent());
}

export async function fetchPlanStepIncomplete(
  workspace: Workspace,
): Promise<boolean> {
  try {
    const progress = await getGuidanceProgress(workspace.id);
    const slice = parseGetStartedSlice(progress?.checklists);
    return isPlanStepIncomplete(slice, workspace);
  } catch {
    // Fail open for network errors unless same-device pending intent exists.
    return Boolean(readPendingWorkspaceIntent());
  }
}

export async function persistPlanConfirmed(workspaceId: string): Promise<void> {
  // Patch only this key — backend merges atomically per checklist field.
  // Do not read-modify-write the whole slice (races with GetStarted flush).
  await updateGuidanceProgress({
    workspaceId,
    checklists: {
      planConfirmed: true,
    },
  });
  clearPendingWorkspaceIntent();
}
